/**
 * Next-step guidance for the booking wizard's "Browse by Instructor" flow.
 *
 * Problem this solves (parent feedback, iPhone): after selecting an instructor
 * the page looked static and the remaining steps weren't obvious. The required
 * order before a slot can be added is service → date → time, so this returns
 * the copy + scroll target for the next *unfilled* step. Keeping it a precise
 * three-step model (rather than a combined "date & time") means the CTA always
 * names the true next action and can anchor-scroll to the right section:
 * the service list, the top of the calendar, or the top of the time slots.
 *
 * The inline CTA, the desktop booking summary, and the mobile footer all read
 * from this one function so their wording and scroll targets never drift.
 * Single-service orgs auto-select their one service, so `serviceChosen` is
 * already true for them and the guidance skips straight to the date step.
 *
 * Pure function so the wording/branching can be unit-tested without React.
 */
export type BookingStepTarget = "service" | "date" | "time";

export interface NextStepGuidance {
  /** Which booking section the CTA should scroll the user to. */
  target: BookingStepTarget;
  /** Secondary line, e.g. "Next: choose a time". */
  headline: string;
  /** Button label, e.g. "Choose a Time". */
  ctaLabel: string;
}

export function nextBookingStep(opts: {
  serviceChosen: boolean;
  dateChosen: boolean;
}): NextStepGuidance {
  if (!opts.serviceChosen) {
    return {
      target: "service",
      headline: "Next: choose a service",
      ctaLabel: "Choose a Service",
    };
  }
  if (!opts.dateChosen) {
    return {
      target: "date",
      headline: "Next: pick a date",
      ctaLabel: "Pick a Date",
    };
  }
  return {
    target: "time",
    headline: "Next: choose a time",
    ctaLabel: "Choose a Time",
  };
}
